import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Wrench, HeartPulse, Truck, Briefcase, PhoneCall,
  ArrowRight, CheckCircle2, Monitor, Landmark, ShoppingBag, Factory, XCircle, Building2,
  Home, Scale, GraduationCap, Car, Zap, Shield, HeartHandshake,
} from 'lucide-react';

const industries = [
  {
    id: 'healthcare',
    icon: HeartPulse,
    name: 'Healthcare & Medical',
    subtitle: 'Clinics, Dental, Physiotherapy',
    problem: 'Patients expect immediate, empathetic responses when dealing with health concerns. In-house receptionists are often overwhelmed managing the waiting room, leading to long hold times or missed appointment bookings.',
    solution: 'Our virtual receptionist team handles patient overflow, new registrations, and out-of-hours emergency triaging with strict confidentiality and a compassionate tone.',
    scenario: 'Your clinic opens at 9 AM, but a patient in pain tries to call at 7:30 AM. We answer the call, securely access your booking portal, and schedule them for the first available slot, providing immediate relief.',
    color: 'bg-rose-50 text-rose-600',
    image: 'https://images.pexels.com/photos/4386467/pexels-photo-4386467.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  },
  {
    id: 'trades',
    icon: Wrench,
    name: 'Trades & Engineering',
    subtitle: 'Plumbers, Electricians, Builders',
    problem: "You're under a sink, up a ladder, or driving between jobs. You can't answer the phone safely or professionally. Every missed call is potentially hundreds or thousands of pounds in lost work going straight to a competitor.",
    solution: 'We provide dedicated telephone answering service coverage. While you work, we capture every lead, book appointments directly into your system, and triage urgent emergency call-outs versus standard quotes.',
    scenario: 'A homeowner has a burst pipe at 8 PM. They Google local plumbers and call you. Instead of voicemail, our operator answers immediately, takes their details, calms them down, and texts you the urgent priority job.',
    color: 'bg-orange-50 text-orange-600',
    image: 'https://images.pexels.com/photos/4483772/pexels-photo-4483772.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  },
  {
    id: 'logistics',
    icon: Truck,
    name: 'Transport & Logistics',
    subtitle: 'Couriers, Haulage, Warehousing',
    problem: 'Drivers and dispatchers are constantly moving. A missed call from a frustrated customer about a delayed delivery or a supplier needing gate access creates massive operational bottlenecks.',
    solution: 'We manage high-volume incoming queries, update customers on tracking statuses based on your provided portals, and maintain clear communication channels between dispatch and clients.',
    scenario: 'A major client calls asking where their pallet is. We answer, check your tracking software, inform them the driver is 15 minutes away, and log the interaction without ever bothering your dispatch team.',
    color: 'bg-blue-50 text-blue-600',
    image: 'https://images.pexels.com/photos/2760343/pexels-photo-2760343.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  },
  {
    id: 'professional',
    icon: Briefcase,
    name: 'Professional Services',
    subtitle: 'Solicitors, Accountants, Consultants',
    problem: 'Solo practitioners and small firms need to project a premium corporate image but cannot justify the £25k+ salary of a dedicated, full-time front desk receptionist.',
    solution: 'We act as your premium front-of-house. We screen unsolicited sales calls, take detailed messages from important clients, and warm-transfer VIPs directly to your mobile.',
    scenario: 'A high-net-worth individual calls your boutique law firm. They are greeted by a polished, professional operator who seamlessly places them on brief hold, announces the call to you, and transfers them through.',
    color: 'bg-slate-50 text-slate-600',
    image: 'https://images.pexels.com/photos/4482900/pexels-photo-4482900.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  },
  {
    id: 'it',
    icon: Monitor,
    name: 'IT & Technology',
    subtitle: 'MSPs, Software Companies, Telecoms',
    problem: 'Support tickets overwhelm small IT teams, and clients expect an instant response. Constant interruptions derail deep technical work.',
    solution: 'Cygnet handles first-line support triage, escalation routing, and out-of-hours emergency calls. We log tickets directly into your service desk.',
    scenario: 'A managed service provider uses Cygnet for out-of-hours calls, ensuring critical infrastructure alerts never go missed while their engineers sleep.',
    color: 'bg-cyan-50 text-cyan-600',
    image: 'https://images.pexels.com/photos/3943947/pexels-photo-3943947.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  },
  {
    id: 'finance',
    icon: Landmark,
    name: 'Finance',
    subtitle: 'Advisors, Wealth Management, Brokers',
    problem: 'Clients call with urgent tax, payroll, and compliance questions. Small firms can\'t staff a full-time receptionist.',
    solution: 'Cygnet screens calls, takes secure messages, and ensures urgent queries are routed immediately to the right partner.',
    scenario: 'An accountancy firm uses Cygnet during self-assessment season when call volumes spike significantly, ensuring no important tax queries are missed.',
    color: 'bg-emerald-50 text-emerald-600',
    image: 'https://images.pexels.com/photos/4199524/pexels-photo-4199524.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  },
  {
    id: 'retail',
    icon: ShoppingBag,
    name: 'Retail & E-commerce',
    subtitle: 'Online Stores, Boutiques, Wholesalers',
    problem: 'Customer queries about orders, returns, and deliveries flood in. One missed call can mean a lost review or chargeback.',
    solution: 'Cygnet handles order queries, provides updates using your tracking system, and reduces complaint escalations.',
    scenario: 'An online retailer uses Cygnet to manage post-purchase queries during peak periods like Christmas, maintaining their excellent Trustpilot score.',
    color: 'bg-pink-50 text-pink-600',
    image: 'https://images.pexels.com/photos/8867415/pexels-photo-8867415.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  },
  {
    id: 'manufacturing',
    icon: Factory,
    name: 'Manufacturing',
    subtitle: 'Engineering, Production, Fabrication',
    problem: 'Suppliers, logistics partners, and clients call with time-sensitive queries. A missed call can delay a production run.',
    solution: 'Cygnet keeps communications flowing, logs supply chain updates, and escalates urgent factory floor calls.',
    scenario: 'A precision engineering firm uses Cygnet for out-of-hours order confirmations and delivery query handling.',
    color: 'bg-amber-50 text-amber-600',
    image: 'https://images.pexels.com/photos/7820376/pexels-photo-7820376.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  },
  {
    id: 'facilities',
    icon: Building2,
    name: 'Facilities & Field Services',
    subtitle: 'Shutters, HVAC, Lifts, Property Maintenance',
    problem: 'When something breaks at a client site, they need an immediate response. Engineers are on the road and cannot manage inbound fault calls, coordinate visits, and keep clients updated simultaneously.',
    solution: 'Cygnet acts as your control centre. We take the fault call, log the job, contact your field team to arrange a visit, and call the client back to confirm — all without the engineer being interrupted.',
    scenario: 'A commercial client calls to report a faulty shutter at one of their sites. We log the fault, reach the on-call engineer, schedule the callout, and phone the client back with a confirmed arrival window — full loop closed.',
    color: 'bg-violet-50 text-violet-600',
    image: 'https://images.pexels.com/photos/8961251/pexels-photo-8961251.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  },
  {
    id: 'property',
    icon: Home,
    name: 'Estate Agents & Property',
    subtitle: 'Lettings, Sales, Property Management',
    problem: 'Prospective buyers and tenants call at all hours. Agents juggling viewings, valuations, and negotiations cannot always answer — and a missed call from a motivated buyer can cost thousands in lost commission.',
    solution: 'Cygnet captures every lead, books viewings directly into your calendar, handles landlord and tenant maintenance queries, and manages out-of-hours urgent property issues.',
    scenario: 'A tenant calls at 7 PM to report a burst pipe. Cygnet answers on behalf of the agency, logs the emergency, contacts the approved plumber, and calls the tenant back with an estimated arrival — protecting both tenant and landlord.',
    color: 'bg-teal-50 text-teal-600',
    image: 'https://images.pexels.com/photos/1546168/pexels-photo-1546168.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  },
  {
    id: 'legal',
    icon: Scale,
    name: 'Legal & Law Firms',
    subtitle: 'Solicitors, Barristers, Legal Consultants',
    problem: 'Clients calling a law firm are often in stressful or time-sensitive situations. A missed call or cold voicemail can damage trust before the relationship has even begun — and risk losing the instruction to a rival firm.',
    solution: 'Cygnet provides a professional, empathetic first point of contact. We take detailed messages, screen unsolicited callers, and warm-transfer high-value clients directly to the right solicitor.',
    scenario: 'A client involved in a time-sensitive property transaction calls outside office hours. Cygnet answers professionally, takes a thorough message, assesses urgency, and alerts the duty solicitor — ensuring nothing falls through the cracks.',
    color: 'bg-stone-50 text-stone-600',
    image: 'https://images.pexels.com/photos/5669619/pexels-photo-5669619.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  },
  {
    id: 'education',
    icon: GraduationCap,
    name: 'Education',
    subtitle: 'Schools, Colleges, Universities, Tutoring',
    problem: 'During admissions periods, term start, or exam season, educational institutions are flooded with calls from parents, students, and applicants. Office staff are overwhelmed and important enquiries go unanswered.',
    solution: 'Cygnet handles overflow enquiry calls, takes absence notifications, books open day appointments, and routes urgent safeguarding calls to the correct staff member immediately.',
    scenario: 'A parent calls to report their child absent at 8:15 AM — before the school office opens. Cygnet takes the absence notification, logs it securely, and sends it to the school\'s safeguarding lead so registration is accurate from the start of day.',
    color: 'bg-yellow-50 text-yellow-600',
    image: 'https://images.pexels.com/photos/8197542/pexels-photo-8197542.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  },
  {
    id: 'automotive',
    icon: Car,
    name: 'Automotive',
    subtitle: 'Dealerships, Garages, Breakdown & Recovery',
    problem: 'A driver broken down on the roadside needs help immediately — not an engaged tone or voicemail. Garages lose service bookings daily to competitors simply because the phone went unanswered during busy workshop hours.',
    solution: 'Cygnet answers every call, books service appointments into your workshop diary, and manages urgent breakdown and recovery requests with clear escalation protocols.',
    scenario: 'A driver calls a recovery firm after breaking down on a motorway at midnight. Cygnet answers immediately, takes their location and vehicle details, dispatches the on-call recovery truck, and stays on the line to reassure the driver until help arrives.',
    color: 'bg-gray-50 text-gray-600',
    image: 'https://images.pexels.com/photos/3807517/pexels-photo-3807517.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  },
  {
    id: 'energy',
    icon: Zap,
    name: 'Energy & Utilities',
    subtitle: 'Suppliers, Contractors, Renewable Energy',
    problem: 'Utility faults and outages don\'t follow business hours. Customers experiencing no power, gas leaks, or heating failures expect an immediate human response — not a recorded message.',
    solution: 'Cygnet provides 24/7 fault line coverage, triaging calls by urgency, dispatching engineers, and keeping affected customers informed throughout. We integrate with your job management systems to log every incident.',
    scenario: 'A residential customer calls at 2 AM reporting no heating in sub-zero temperatures with elderly residents in the property. Cygnet identifies the priority, escalates to the on-call engineer, and provides the customer with an ETA — preventing a welfare situation.',
    color: 'bg-lime-50 text-lime-600',
    image: 'https://images.pexels.com/photos/1108572/pexels-photo-1108572.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  },
  {
    id: 'security',
    icon: Shield,
    name: 'Security & Alarm Companies',
    subtitle: 'Intruder Alarms, CCTV, Access Control',
    problem: 'Alarm activations and security incidents happen at any hour. Response companies need an always-on answering service that can make rapid decisions, contact key holders, and dispatch response teams without delay.',
    solution: 'Cygnet provides a 24/7 alarm response answering service. We follow your exact escalation protocols — contacting key holders in order, dispatching response units, and notifying the police where required.',
    scenario: 'An intruder alarm activates at a commercial premises at 3 AM. Cygnet answers the monitoring alert, works through the key holder list, reaches the owner, coordinates the response team, and logs every action with timestamps for the incident report.',
    color: 'bg-red-50 text-red-700',
    image: 'https://images.pexels.com/photos/5699823/pexels-photo-5699823.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  },
  {
    id: 'charity',
    icon: HeartHandshake,
    name: 'Charities & Helplines',
    subtitle: 'Non-profits, Support Organisations, Foundations',
    problem: 'Charities and support organisations often operate with limited staff and tight budgets, yet they serve some of the most vulnerable callers. A missed call to a helpline can have serious consequences.',
    solution: 'Cygnet provides compassionate, trained call handling for charities. We take donation enquiries, answer general queries, and handle sensitive support calls with the care and discretion they require.',
    scenario: 'A charity running a befriending service for isolated elderly people uses Cygnet to handle inbound calls from potential volunteers and referrals from social services — ensuring every contact is captured and responded to warmly.',
    color: 'bg-rose-50 text-rose-600',
    image: 'https://images.pexels.com/photos/6646918/pexels-photo-6646918.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  },
];

const Industries = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.replace('#', ''));
    if (el) setTimeout(() => {
      const top = el.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top, behavior: 'smooth' });
    }, 100);
  }, [hash]);

  return (
  <div className="pb-12">
    {/* Header */}
    <section className="bg-white py-16 md:py-24 border-b border-gray-200 relative overflow-hidden">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cygnet-dark/10 text-cygnet-dark font-medium text-sm mb-6"
        >
          Industry Expertise
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
        >
          We speak your <span className="text-cygnet-dark">language.</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-xl text-gray-500"
        >
          Different sectors require different approaches. From emergency plumbing call-outs to sensitive medical inquiries, our operators are trained to handle your specific operational needs.
        </motion.p>
      </div>
    </section>

    {/* Industry cards */}
    <section className="py-12 md:py-16 bg-gray-50">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="space-y-12">
          {industries.map((ind, index) => (
            <motion.div
              key={ind.id}
              id={ind.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              viewport={{ once: true, margin: '-100px' }}
              className={`bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-sm scroll-mt-28 border-l-4 ${index % 2 === 0 ? 'border-l-cygnet-dark' : 'border-l-blue-400'}`}
            >
              <div className="flex flex-col lg:flex-row">
                {/* Content */}
                <div className="lg:w-[60%] p-8 md:p-12 order-2 lg:order-1">
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${ind.color}`}>
                      <ind.icon className="w-7 h-7" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{ind.name}</h2>
                      <p className="text-gray-500 font-medium">{ind.subtitle}</p>
                    </div>
                  </div>
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <XCircle className="w-6 h-6 text-red-500" /> The Problem
                      </h3>
                      <p className="text-gray-500 leading-relaxed">{ind.problem}</p>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <CheckCircle2 className="w-6 h-6 text-cygnet-dark" /> How Cygnet Helps
                      </h3>
                      <p className="text-gray-500 leading-relaxed">{ind.solution}</p>
                    </div>
                  </div>
                </div>

                {/* Scenario panel */}
                <div className="lg:w-[40%] min-h-[300px] flex flex-col justify-center relative overflow-hidden order-1 lg:order-2">
                  <img src={ind.image} alt={ind.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-slate-900/75" />
                  <div className="relative z-10 p-8 md:p-12">
                    <div className="inline-block bg-cygnet-dark text-white rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider mb-6">
                      Real-world Scenario
                    </div>
                    <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 shadow-xl relative">
                      <div className="absolute -left-3 -top-3 w-8 h-8 bg-cygnet-dark rounded-full flex items-center justify-center text-white shadow-lg">
                        <PhoneCall className="w-4 h-4" />
                      </div>
                      <p className="italic text-lg leading-relaxed text-white">"{ind.scenario}"</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-16 md:py-24 bg-white">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Don't see your industry?</h2>
        <p className="text-lg text-gray-500 mb-8">
          We work with dozens of niches. Because we act as a true extension of your team, we can adapt our call handling protocols to suit almost any professional business model.
        </p>
        <Link to="/contact" className="inline-flex items-center justify-center rounded-md bg-cygnet-dark px-8 py-4 text-base font-semibold text-white hover:bg-cygnet-DEFAULT transition-colors">
          Let's Discuss Your Needs <ArrowRight className="ml-2 w-5 h-5" />
        </Link>
      </div>
    </section>
  </div>
  );
};

export default Industries;
